import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import identificationIcon from "@assets/images/kyc/Identification-icon.svg";
import { useServices } from "@core/servicesContext";
import { countriesIndicators, CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { allCountrycodes, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { AreaExpertise } from "@domain/userKyc/areaExpertise";
import { allIdentityType, IdentityType, UserKyc } from "@domain/userKyc/userKyc";
import { UserKycDraft } from "@domain/userKyc/userKycDraft";
import { useAsync } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { FileReminderInfo } from "@ui/pages/application/applicationEditorPage/fileReminderInfo";
import { theme } from "@ui/styles/theme";
import { alpha, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { Description } from "@ui/components/containers/description";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { PlusIconRound, RemoveIconRound } from "@ui/components/icons";
import { AreaExpertiseSelector } from "@ui/components/inputs/areaExpertiseSelector";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { Selecter } from "@ui/components/inputs/selecter";
import { Row, Stack, stack } from "@ui/components/layout";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { UserKycPhonenumberCheckingModal } from "./userKycPhonenumberCheckingModal";
import { UserSelector } from "./userTypeSelector";

type UserKycForm = Omit<UserKycDraft, "countryCode" | "type"> & {
	type:
		| {
				typeKyc: "PROFESSIONAL";
				areaExpertise: "" | AreaExpertise;
		  }
		| {
				typeKyc: "TEACHER";
				matricule: string;
		  };
};

interface UserKycEditionModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	onKycChange: (userKyc: UserKyc) => void;
}
export const UserKycEditionModal: React.FC<UserKycEditionModalProps> = ({ close, onKycChange, ...htmlProps }) => {
	const { userKycService, trackingService } = useServices();
	const currentProfile = useCurrentProfile();

	const [userKyc, setUserKyc] = useState<UserKycForm>({
		firstName: "",
		lastName: "",
		phoneNumber: "",

		indicative: "",
		email: "",
		address: "",
		type: {
			typeKyc: "PROFESSIONAL",
			areaExpertise: "",
		},
		identity: {
			typePassport: undefined,
			idPassportId: undefined,
			idPassportAsset: undefined,
		},
	});

	const [country, setCountry] = useState<Country | undefined>();
	const [isPhonenumberChecked, setIsPhonenumberChecked] = useState<boolean>(false);

	const invalidUserKycFirstName = userKyc.firstName.length === 0;
	const invalidUserKycLastName = userKyc.lastName.length === 0;
	const invalidUserKycAddress = userKyc.address.length === 0;
	const invalidUserKycPhoneNumber = userKyc.phoneNumber.length === 0;
	const invalidUserKycIndicative = userKyc.indicative.length === 0;
	const invalidUserKycEmail = userKyc.email.length === 0;

	const invalidAreaExpertise = userKyc.type.typeKyc === "PROFESSIONAL" && userKyc.type.areaExpertise.length === 0;
	const invalidUserKycIdentity =
		!userKyc.identity.idPassportAsset || !userKyc.identity.idPassportId || !userKyc.identity.typePassport;

	const { createModal } = useModalTools();

	const openConfirmation = (phoneNumber: string, indicative: CountryIndicator): void => {
		createModal((close) => (
			<UserKycPhonenumberCheckingModal
				onNumberChecked={(isChecked) => setIsPhonenumberChecked(isChecked)}
				phoneNumber={phoneNumber}
				indicative={indicative}
				close={close}
			/>
		));
	};

	const { run, loading } = useAsync(async (): Promise<UserKyc> => {
		const newKyc = await userKycService.saveUserKyc({
			...userKyc,
			countryResidence: countryCodes[country as Country],
			type:
				userKyc.type.typeKyc === "PROFESSIONAL"
					? {
							typeKyc: "PROFESSIONAL",
							areaExpertise: userKyc.type.areaExpertise === "" ? "management" : userKyc.type.areaExpertise,
						}
					: userKyc.type,
		});
		return newKyc;
	}, [userKyc, country]);

	return (
		<Container keyboardEventDisabled close={close} {...htmlProps}>
			<div />
			<Stack align="center" justify="center" gap={20}>
				<img src={identificationIcon} />
				<StyledTitle>{I18n.t("kyc.edit.modal.title")}</StyledTitle>
				<StyledDescription>{I18n.t("kyc.edit.modal.description")}</StyledDescription>
			</Stack>

			<Stack align="center" gap={20}>
				<PrimaryBold>{I18n.t("kyc.edit.user_type.title")}</PrimaryBold>
				<UserSelector
					value={userKyc.type.typeKyc}
					onClick={(value) =>
						setUserKyc((current) => {
							return value === "PROFESSIONAL"
								? { ...current, type: { typeKyc: "PROFESSIONAL", areaExpertise: "" } }
								: { ...current, type: { typeKyc: value, matricule: "" } };
						})
					}
				/>
			</Stack>
			<Stack style={{ width: "100%" }} gap={30}>
				{userKyc.type.typeKyc === "TEACHER" && (
					<InfoEdition>
						<PrimaryMedium>{I18n.t("kyc.edit.TEACHER.matricule")}</PrimaryMedium>
						<TextInput
							value={userKyc.type.matricule ?? ""}
							onChange={(e) => {
								const value = e.currentTarget.value;
								setUserKyc((current) => ({
									...current,
									type: {
										...current.type,
										matricule: value,
									},
								}));
							}}
						/>
					</InfoEdition>
				)}
				{userKyc.type.typeKyc === "PROFESSIONAL" && (
					<InfoEdition>
						<PrimaryMedium>{I18n.t("kyc.edit.PROFESSIONAL.area")}</PrimaryMedium>
						<AreaExpertiseSelector
							value={userKyc.type.areaExpertise}
							onChange={(value) => {
								setUserKyc((current) => ({
									...current,
									type: {
										...current.type,
										areaExpertise: value,
									},
								}));
							}}
						/>
					</InfoEdition>
				)}
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.lastname")}</PrimaryMedium>
					<TextInput
						value={userKyc.lastName ?? ""}
						onChange={(e) => {
							const value = e.currentTarget.value;
							setUserKyc((current) => ({
								...current,
								lastName: value,
							}));
						}}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.firstname")}</PrimaryMedium>
					<TextInput
						value={userKyc?.firstName}
						onChange={(e) => {
							const value = e.currentTarget.value;
							setUserKyc((current) => ({ ...current, firstName: value }));
						}}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.country")}</PrimaryMedium>
					<CountryDatalist
						id="countries"
						value={country}
						onChange={(v) => {
							setCountry(v);
							if (countriesIndicators[countryCodes[v]]) {
								setUserKyc((current) => ({ ...current, indicative: countriesIndicators[countryCodes[v]] }));
							}
						}}
						options={allCountrycodes}
					/>
				</InfoEdition>
				<Row gap={40} align="flex-end">
					<InfoEdition>
						<PrimaryMedium>{I18n.t("kyc.edit.phone_number.label")}</PrimaryMedium>
						<StyledBlocIndicator>
							<IndicatorDataList
								onChange={(value) => setUserKyc((current) => ({ ...current, indicative: value }))}
								value={userKyc?.indicative}
							/>
							<TextInput
								value={userKyc?.phoneNumber}
								onChange={(e) => {
									const value = e.currentTarget.value;
									setUserKyc((current) => ({ ...current, phoneNumber: value }));
								}}
							/>
						</StyledBlocIndicator>
					</InfoEdition>
					<StyledTertiaryButton
						isPhonenumberChecked={isPhonenumberChecked}
						onClick={async () => {
							try {
								if (!userKyc?.phoneNumber || !userKyc.indicative || isPhonenumberChecked) return;
								openConfirmation(userKyc.phoneNumber, userKyc.indicative);
								await userKycService.checkNumber(userKyc.indicative, userKyc.phoneNumber);
							} catch (error) {
								console.log("error", error);
							}
						}}
					>
						{I18n.t(isPhonenumberChecked ? "checked" : "check")}
					</StyledTertiaryButton>
				</Row>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.email")}</PrimaryMedium>
					<TextInput
						value={userKyc.email ?? ""}
						onChange={(e) => {
							const value = e.currentTarget.value;
							setUserKyc((current) => ({
								...current,
								email: value,
							}));
						}}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.address")}</PrimaryMedium>
					<TextInput
						value={userKyc.address ?? ""}
						onChange={(e) => {
							const value = e.currentTarget.value;
							setUserKyc((current) => ({
								...current,
								address: value,
							}));
						}}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.identity.type")}</PrimaryMedium>

					<Selecter
						value={userKyc.identity.typePassport}
						onChange={(value) =>
							setUserKyc((current) => ({
								...current,
								identity: {
									...current.identity,
									typePassport: value as IdentityType,
								},
							}))
						}
						options={allIdentityType.map((val) => ({
							label: I18n.t(`kyc.edit.identity.${val}.label`),
							value: val,
						}))}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.identity.number")}</PrimaryMedium>
					<TextInput
						value={userKyc.identity.idPassportId ?? ""}
						onChange={(e) => {
							const value = e.currentTarget.value;
							setUserKyc((current) => ({
								...current,
								identity: {
									...current.identity,
									idPassportId: value,
								},
							}));
						}}
					/>
				</InfoEdition>
				<InfoEdition>
					<PrimaryMedium>{I18n.t("kyc.edit.identity.proof_of_identity.label")}</PrimaryMedium>
					<FormField>
						<FileReminderInfo fileSize={2} acceptedFormat="document" />
						<StyledFileUplaod
							type="document"
							onUpload={(media) =>
								setUserKyc((current) => ({
									...current,
									identity: {
										...current.identity,
										idPassportAsset: media,
									},
								}))
							}
						>
							{(_, loading) => (
								<div className="fileUpload">
									<StyledPlaceholder>{I18n.t("kyc.edit.identity.proof_of_identity.placeholder")}</StyledPlaceholder>
									{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
								</div>
							)}
						</StyledFileUplaod>
						{userKyc.identity.idPassportAsset && (
							<StyledFilesContainer>
								<StyledFileInfo>
									{userKyc.identity.idPassportAsset?.displayName}
									<RemoveIconRound
										onClick={() =>
											setUserKyc((current) => ({
												...current,
												identity: {
													...current.identity,
													idPassportAsset: undefined,
												},
											}))
										}
									/>
								</StyledFileInfo>
							</StyledFilesContainer>
						)}
					</FormField>
				</InfoEdition>
			</Stack>
			<LoadingButton
				onClick={async () => {
					try {
						trackingService.updateProfile(currentProfile?.id as string, {
							phonenumber: userKyc.phoneNumber,
							user_type: userKyc.type.typeKyc,
							field_of_competence: userKyc.type.typeKyc === "PROFESSIONAL" ? userKyc.type.areaExpertise : undefined,
						});
						const kyc = await run();
						onKycChange(kyc);
						close();
					} catch {
						createToast(I18n.t("error"), "creation de qyc error", "error");
					}
				}}
				loading={loading}
				disabled={
					invalidAreaExpertise ||
					invalidUserKycIdentity ||
					invalidUserKycAddress ||
					invalidUserKycFirstName ||
					invalidUserKycLastName ||
					invalidUserKycPhoneNumber ||
					invalidUserKycIndicative ||
					invalidUserKycEmail ||
					!isPhonenumberChecked ||
					!country ||
					!(country in countryCodes)
				}
			>
				{I18n.t("save")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	${gap(40)};

	> button {
		margin: 0 auto;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 70%;
`;

const StyledBlocIndicator = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	width: 100%;
	grid-gap: 20px;
`;

const TextInput = styled.input<{ isInvalid?: boolean }>`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 10px 12px 9px;
	border-radius: 10px;
	font-size: 0.935rem;
	outline: none;

	:disabled {
		color: ${({ theme }) => theme.colors.textTertiary};
	}

	:focus {
		border: 1px solid ${({ theme, isInvalid }) => (isInvalid ? theme.colors.red : theme.colors.secondary)};
	}

	${({ isInvalid }) =>
		isInvalid &&
		css`
			border: 1px solid ${({ theme }) => theme.colors.red};
		`}

	@media (max-width: 370px) {
		width: 140px;
	}
`;

const StyledFileUplaod = styled(FileUpload)`
	.fileUpload {
		display: flex;
		align-items: center;
		border-radius: 7px;
		align-content: flex-end;
		justify-content: space-between;
		padding: 11px 15px;
		background-color: "rgba(" #f3f4f6 ",0.4)";
		background-color: rgba(243, 244, 246, 0.4);
		border: 1.5px solid #e9e9e9;
	}
`;

const StyledPlaceholder = styled.div`
	color: #8b8b8b;
	font-size: 14px;
	font-weight: 500;
`;

const StyledFilesContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-gap: 20px;
`;
const FormField = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 100%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledFileInfo = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: ${alpha("#BEDAFF", 0.5)};
	padding: 10px;
	font-size: 14px;
	border-radius: 7px;
	gap: 10px;
`;

const StyledTertiaryButton = styled(TertiaryButton)<{ isPhonenumberChecked: boolean }>`
	color: ${({ theme, isPhonenumberChecked }) => (isPhonenumberChecked ? theme.colors.green : theme.colors.secondary)};
`;

const StyledDescription = styled(Description)`
	align-content: justify;
`;

const StyledTitle = styled(PrimaryBold)`
	font-size: 24px;
`;
